import { useMemo } from 'react'
import {
  DEFAULT_MAP_APPEARANCE,
  MAP_APPEARANCE_STORAGE_KEY,
  clampAppearance,
  type MapAppearance,
} from './mapAppearance'
import { useLocalStorage } from './useLocalStorage'

/**
 * The appearance a hunt map draws with: the default treatment, plus the layer
 * switches this browser has saved on the team map.
 *
 * The hunt maps (planning a hunt, the invitation, the report, the replay) have no
 * view-settings panel of their own, so they used to draw `DEFAULT_MAP_APPEARANCE`
 * outright, and a forbidden area the user had hidden on the team map came back the
 * moment they opened a hunt. The app applies its layer switches in the hunt view
 * too, so the layers follow the same saved choice here.
 *
 * Only the layer switches carry over. The border treatment, sizes and opacities
 * stay at their defaults: no hunt surface offers a control for them, and a filled
 * or reverse wash the user picked for the team map would restyle a hunt map they
 * have no way to restyle back from where they are.
 */
export function useHuntMapAppearance(): MapAppearance {
  // Read-only: no hunt surface writes the setting.
  const [raw] = useLocalStorage<MapAppearance>(MAP_APPEARANCE_STORAGE_KEY, DEFAULT_MAP_APPEARANCE)
  const { forbiddenAreasVisible, subAreasVisible, arrowsVisible, linesVisible } = clampAppearance(raw)
  return useMemo(
    () => ({ ...DEFAULT_MAP_APPEARANCE, forbiddenAreasVisible, subAreasVisible, arrowsVisible, linesVisible }),
    [forbiddenAreasVisible, subAreasVisible, arrowsVisible, linesVisible],
  )
}
